import { describe_error } from '../../utils/ErrorDescriber';
import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';

import { cents_to_dollar_text, dollar_text_to_cents } from '../../utils/Money';
import { get_billing_interval_label } from './BillingIntervals';

const c_DefaultCurrency = 'USD';

// Adds a subscription plan, or edits one when plan is given. options holds the choices the backend offers
// (billing_intervals), chosen with a Monthly / Annual toggle that starts on default_billing_interval for a new plan.
// on_save(plan_data) returns a promise and rejects with an Error carrying the message to show. The price is entered
// in dollars and saved as whole cents.
function PlanModal({plan, options, default_billing_interval, on_save, on_close}){
	const is_edit = plan ? true : false;

	const [name, set_name] = useState(is_edit ? plan.name : '');
	const [description, set_description] = useState((is_edit && plan.description) ? plan.description : '');
	const [price_text, set_price_text] = useState(is_edit ? cents_to_dollar_text(plan.price_in_cents) : '');
	const [currency, set_currency] = useState((is_edit && plan.currency) ? plan.currency : c_DefaultCurrency);
	const [billing_interval, set_billing_interval] = useState(is_edit ? plan.billing_interval : default_billing_interval);
	const [is_active, set_is_active] = useState(is_edit ? plan.is_active : true);
	const [sort_order, set_sort_order] = useState(is_edit ? String(plan.sort_order) : '0');
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');

	// The plan as the backend takes it, or null with the error shown.
	const get_plan_data = ()=>{
		if(name.trim().length === 0){
			set_error('Plan name is required.');
			return null;
		}

		const price_in_cents = dollar_text_to_cents(price_text);
		if(price_in_cents === null){
			set_error('Enter the price in dollars, such as 19.99.');
			return null;
		}

		if(!/^\d+$/.test(sort_order.trim())){
			set_error('Sort order must be a whole number of 0 or more.');
			return null;
		}

		return {
			name: name.trim(),
			description: description.trim(),
			price_in_cents,
			currency: currency.trim().toUpperCase(),
			billing_interval,
			is_active,
			sort_order: Number(sort_order.trim())
		};
	};

	const handle_save = async(event)=>{
		event.preventDefault();

		const plan_data = get_plan_data();
		if(!plan_data){
			return;
		}

		set_error('');
		set_saving(true);

		try {
			await on_save(plan_data);
		}
		catch (error) {
			console.log('PlanModal/handle_save - ERROR: ', describe_error(error));
			set_error(error.message);
			set_saving(false);
		}
	};

	const render_save_label = ()=>{
		if(saving){
			return (
				<Spinner animation="border" size="sm"/>
			);
		}

		return is_edit ? 'Save' : 'Add Plan';
	};

	return (
		<Modal show={true} onHide={on_close} centered>
			<Modal.Header closeButton>
				<Modal.Title>{is_edit ? 'Edit Plan' : 'Add Plan'}</Modal.Title>
			</Modal.Header>

			<Form onSubmit={handle_save}>
				<Modal.Body>
					{error && <Alert variant="danger">{error}</Alert>}

					<Form.Group className="subscription-modal-field">
						<Form.Label>Name</Form.Label>
						<Form.Control
							type="text"
							value={name}
							disabled={saving}
							onChange={(event)=>{ set_name(event.target.value); }}/>
					</Form.Group>

					<Form.Group className="subscription-modal-field">
						<Form.Label>Description</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							value={description}
							disabled={saving}
							onChange={(event)=>{ set_description(event.target.value); }}/>
					</Form.Group>

					<h6 className="subscription-modal-section-title">Billing</h6>
					<ToggleButtonGroup
						type="radio"
						name="plan-billing-interval"
						className="subscription-modal-toggle"
						value={billing_interval}
						onChange={(value)=>{ set_billing_interval(value); }}>
						{options.billing_intervals.map((interval)=>{
							return (
								<ToggleButton
									key={interval}
									id={'plan-billing-interval-' + interval}
									variant="outline-primary"
									value={interval}
									disabled={saving}>
									{get_billing_interval_label(interval)}
								</ToggleButton>
							);
						})}
					</ToggleButtonGroup>

					<div className="subscription-modal-row">
						<Form.Group className="subscription-modal-field">
							<Form.Label>{'Price per ' + billing_interval + ' (dollars)'}</Form.Label>
							<Form.Control
								type="text"
								value={price_text}
								placeholder="19.99"
								disabled={saving}
								onChange={(event)=>{ set_price_text(event.target.value); }}/>
						</Form.Group>

						<Form.Group className="subscription-modal-field">
							<Form.Label>Currency</Form.Label>
							<Form.Control
								type="text"
								value={currency}
								maxLength={3}
								disabled={saving}
								onChange={(event)=>{ set_currency(event.target.value); }}/>
						</Form.Group>

					</div>

					<div className="subscription-modal-row">
						<Form.Group className="subscription-modal-field">
							<Form.Label>Sort order</Form.Label>
							<Form.Control
								type="text"
								value={sort_order}
								disabled={saving}
								onChange={(event)=>{ set_sort_order(event.target.value); }}/>
						</Form.Group>

						<Form.Group className="subscription-modal-field subscription-modal-check">
							<Form.Check
								type="checkbox"
								label="Active"
								checked={is_active}
								disabled={saving}
								onChange={(event)=>{ set_is_active(event.target.checked); }}/>
						</Form.Group>
					</div>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" type="button" disabled={saving} onClick={on_close}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={saving}>
						{render_save_label()}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default PlanModal;
